import { SelectOptionType } from '@octano/global-ui';
import { ReactElement, useCallback } from 'react';
import { createFetchContext } from '../../components/contexts/FetchContextFactory';
import DisplayError from '../../components/info/DisplayError';
import Loading from '../../components/info/Loading';
import { formatOptions } from '../../utils/selectFormat';
import { getAsyncOptionsFields } from './api';
import { AsynOptionsFieldsOrigin, OptionsResponse } from './types';

interface AsyncOptionsFieldsLoaderProps {
  from: AsynOptionsFieldsOrigin;
  children: (props: {
    identificationTypeOptions: SelectOptionType[];
    refresh: () => Promise<void>;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  OptionsResponse
>();

export function useAsyncOptionsFieldsLoader() {
  return useFetch();
}

export const BaseLoaderConsumer = FetchConsumer;

export default function AsyncOptionsFieldsLoader({
  from,
  children,
}: AsyncOptionsFieldsLoaderProps) {
  const request = useCallback(() => {
    return getAsyncOptionsFields(from);
  }, [from]);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return (
              <div className="mx-3">
                <Loading insideCard />
              </div>
            );
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children({
            identificationTypeOptions: formatOptions(
              data?.identificationTypes ?? [],
            ),
            refresh,
          });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
